import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ADMIN_QUERY_KEYS, pendingBusinessesQueryOptions } from '@/api/queries/admin';
import { BUSINESSES_QUERY_KEYS } from '@/api/queries/businesses';
import * as adminApi from '@/api/endpoints/admin';
import { ListPendingBusinessesParams } from '@/api/endpoints/admin';

/**
 * Query hook for listing pending businesses
 */
export function useAdminPendingBusinesses(params: ListPendingBusinessesParams = {}) {
  return useQuery(pendingBusinessesQueryOptions(params));
}

/**
 * Mutation hook for approving a business
 */
export function useAdminApproveBusiness() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return adminApi.approveBusiness(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.pendingBusinesses() });
      queryClient.invalidateQueries({ queryKey: BUSINESSES_QUERY_KEYS.all });
    },
  });
}

/**
 * Mutation hook for updating a business
 */
export function useAdminUpdateBusiness() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Record<string, any>;
    }) => {
      return adminApi.updateBusiness(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.pendingBusinesses() });
      queryClient.invalidateQueries({ queryKey: BUSINESSES_QUERY_KEYS.all });
    },
  });
}

/**
 * Mutation hook for deleting a business
 */
export function useAdminDeleteBusiness() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return adminApi.deleteBusiness(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.pendingBusinesses() });
      queryClient.invalidateQueries({ queryKey: BUSINESSES_QUERY_KEYS.all });
    },
  });
}
